// AI.2 — Consultor IA server functions (hardened).
//
// Aplica: contratos de resposta com Zod (+1 reparo), idempotência,
// concorrência otimista, rate limits, feature flags, ações registradas
// no registry, telemetria de custo/latência, revisão determinística.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { AIError, estimateCostUsd, llmChat, type LLMResult } from "./provider.server";
import { buildAIContext, type AIContext } from "./context-builder.server";
import {
  buildOpeningMessage,
  buildReviewPrompt,
  buildSystemPrompt,
  extractJson,
  repairPrompt,
  type ChatIntent,
  type ReviewReport,
} from "./prompt-builder.server";
import {
  AIConversationalReplySchema,
  AIReviewSchema,
  AI_ERROR_MESSAGES,
  AI_LIMITS,
} from "./contracts";
import type { AIFieldSuggestion } from "./contracts";
import { isActionAllowed } from "./actions-registry.server";
import type { BlockKey } from "@/lib/business-blocks";
import { getStructuredConfig, serializeFieldValue } from "@/lib/business-plan/structured-fields";
import {
  runAllChecks,
  consistencyScore,
  findCriticalConflictsForSuggestion,
  type BusinessSnapshot,
  type ConsistencyIssue,
} from "./consistency-checks";
import {
  summarizeConversation,
  ConversationSummarySchema,
  type ConversationSummary,
} from "./summarization.server";
// SDE.4 · Onda 4.3a — Gate de quota EXTERNO. Não altera contratos do HIA.1;
// inserido apenas antes de chamar llmChat.
import { consumeIaQuota, type QuotaGateResult } from "@/lib/ia/quota";
// SDE.4 · Onda 4.3b — Rate-limit anti-abuso INDEPENDENTE da quota.
// Precedência: rate-limit ANTES da quota (docs/IA_RATELIMIT_DECISION.md §5).
import { checkIaRateLimit, type RateLimitResult } from "@/lib/ia/ratelimit";

// ---------- helpers ----------

function friendlyError(e: unknown): Error {
  if (e instanceof AIError) return new Error(AI_ERROR_MESSAGES[e.type] ?? e.message);
  return e instanceof Error ? e : new Error(String(e));
}

async function getActiveProjectId(supabase: any, userId: string): Promise<string | null> {
  const { data } = await supabase
    .from("user_projects")
    .select("id")
    .eq("user_id", userId)
    .eq("status", "active")
    .maybeSingle();
  return data?.id ?? null;
}

async function trackAI(supabase: any, userId: string, event: string, meta: Record<string, unknown>) {
  try {
    await supabase.from("analytics_events").insert({
      user_id: userId,
      event_name: event,
      metadata: meta as never,
    });
  } catch { /* non-fatal */ }
}

async function logAIAction(supabase: any, row: {
  userId: string;
  projectId: string | null;
  conversationId?: string | null;
  actionType: string;
  mode: string;
  payload?: unknown;
  result?: unknown;
  llm?: LLMResult | null;
  success: boolean;
  errorType?: string | null;
}) {
  const cost = row.llm ? estimateCostUsd(row.llm.model, row.llm.inputTokens, row.llm.outputTokens) : null;
  try {
    await supabase.from("ai_actions_log").insert({
      user_id: row.userId,
      project_id: row.projectId,
      conversation_id: row.conversationId ?? null,
      action_type: row.actionType,
      mode: row.mode,
      payload: (row.payload ?? {}) as never,
      result: (row.result ?? {}) as never,
      provider: row.llm?.provider ?? null,
      model: row.llm?.model ?? null,
      input_tokens: row.llm?.inputTokens ?? null,
      output_tokens: row.llm?.outputTokens ?? null,
      total_tokens: row.llm?.totalTokens ?? null,
      latency_ms: row.llm?.latencyMs ?? null,
      cost_estimate: cost,
      success: row.success,
      error_type: row.errorType ?? null,
    });
  } catch { /* non-fatal */ }
}

// Feature-flag check (server-side, sem hash: aqui basta enabled global).
async function requireFlag(supabase: any, key: string) {
  const { data } = await supabase
    .from("feature_flags")
    .select("enabled")
    .eq("key", key)
    .maybeSingle();
  if (!data || data.enabled === false) {
    throw new Error("Este recurso do Consultor IA está temporariamente desativado.");
  }
}

async function isFlagOn(supabase: any, key: string): Promise<boolean> {
  const { data } = await supabase
    .from("feature_flags")
    .select("enabled")
    .eq("key", key)
    .maybeSingle();
  return !!(data && data.enabled !== false);
}

// Rate limits (simples e determinísticos por contagem em ai_messages).
async function enforceRateLimits(supabase: any, userId: string, kind: "message" | "review") {
  const now = new Date();
  if (kind === "message") {
    const oneMin = new Date(now.getTime() - 60_000).toISOString();
    const oneDay = new Date(now.getTime() - 86_400_000).toISOString();
    const [{ count: perMin }, { count: perDay }] = await Promise.all([
      supabase.from("ai_messages").select("id", { count: "exact", head: true })
        .eq("user_id", userId).eq("role", "user").gte("created_at", oneMin),
      supabase.from("ai_messages").select("id", { count: "exact", head: true })
        .eq("user_id", userId).eq("role", "user").gte("created_at", oneDay),
    ]);
    if ((perMin ?? 0) >= AI_LIMITS.MESSAGES_PER_MINUTE) {
      throw new Error("Você enviou muitas mensagens em pouco tempo. Aguarde um minuto.");
    }
    if ((perDay ?? 0) >= AI_LIMITS.MESSAGES_PER_DAY) {
      throw new Error("Limite diário de mensagens do consultor atingido. Tente amanhã.");
    }
  } else {
    const oneDay = new Date(now.getTime() - 86_400_000).toISOString();
    const { count } = await supabase.from("ai_actions_log").select("id", { count: "exact", head: true })
      .eq("user_id", userId).eq("action_type", "review_plan").gte("created_at", oneDay);
    if ((count ?? 0) >= AI_LIMITS.REVIEWS_PER_DAY) {
      throw new Error("Limite diário de revisões atingido. Tente amanhã.");
    }
  }
}

// Contexto -> hash simples para snapshot de concorrência.
function hashContext(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16);
}

// Parse de resposta com 1 reparo.
async function parseWithRepair<T>(
  raw: string,
  schema: z.ZodType<T>,
  repairFn: () => Promise<LLMResult>,
): Promise<{ value: T; llm?: LLMResult } | { error: "invalid" }> {
  const tryParse = (txt: string): T | null => {
    try { return schema.parse(JSON.parse(extractJson(txt))); } catch { return null; }
  };
  const first = tryParse(raw);
  if (first) return { value: first };
  let repaired: LLMResult;
  try { repaired = await repairFn(); } catch { return { error: "invalid" }; }
  const second = tryParse(repaired.text);
  if (second) return { value: second, llm: repaired };
  return { error: "invalid" };
}

// ---------- types ----------

export type StartAIConversationInput = {
  sectionKey?: BlockKey | null;
  fieldKey?: string | null;
  intent?: ChatIntent;
};

export type AIConversationMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  suggestion?: {
    id: string;
    text: string;
    sectionKey: string | null;
    fieldKey: string | null;
    confidence?: "high" | "medium" | "low";
    warnings?: string[];
    conflict?: boolean;
  } | null;
  createdAt: string;
};

export type AIConversationSummaryView = {
  goal: string;
  confirmedFacts: string[];
  openQuestions: string[];
  nextStep: string;
  version: number;
} | null;

export type AIConversationView = {
  conversationId: string;
  title: string;
  sectionKey: string | null;
  fieldKey: string | null;
  messages: AIConversationMessage[];
  context: AIContext;
  summary?: AIConversationSummaryView;
};

// ---------- start conversation ----------

export const startAIConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: StartAIConversationInput) => d ?? {})
  .handler(async ({ data, context }): Promise<AIConversationView> => {
    const { supabase, userId } = context;
    await requireFlag(supabase, "ai_consultant_enabled");

    const projectId = await getActiveProjectId(supabase, userId);
    const ctx = await buildAIContext(supabase, userId, {
      projectId,
      sectionKey: data.sectionKey ?? null,
      fieldKey: data.fieldKey ?? null,
    });

    const intent: ChatIntent = data.intent ?? (data.fieldKey ? "field_suggestion" : "conversation");
    const opening = buildOpeningMessage(ctx, intent);
    const title = ctx.section
      ? `${ctx.section.title}${data.fieldKey ? ` · ${ctx.section.requiredFields.find((f) => f.key === data.fieldKey)?.label ?? data.fieldKey}` : ""}`
      : "Consultor IA";

    const { data: conv, error } = await supabase
      .from("ai_conversations")
      .insert({
        user_id: userId,
        project_id: projectId,
        section_key: data.sectionKey ?? null,
        field_key: data.fieldKey ?? null,
        title,
        context_snapshot: ctx as never,
      })
      .select("id, title, section_key, field_key")
      .single();
    if (error) throw error;

    const { data: msg, error: msgErr } = await supabase
      .from("ai_messages")
      .insert({
        conversation_id: conv.id,
        user_id: userId,
        role: "assistant",
        content: opening,
      })
      .select("id, created_at")
      .single();
    if (msgErr) throw msgErr;

    await trackAI(supabase, userId, "ai_opened", {
      conversation_id: conv.id,
      section_key: data.sectionKey ?? null,
      field_key: data.fieldKey ?? null,
      intent,
    });

    // HIA.1 — resolve/entra na coorte de homologação (idempotente, fail-safe).
    if (projectId) {
      try {
        const { resolveHomologationCohortImpl } = await import("./homologation.functions");
        await resolveHomologationCohortImpl(supabase, userId, { projectId });
      } catch { /* telemetry only — não bloqueia conversa */ }
    }

    return {
      conversationId: conv.id,
      title: conv.title,
      sectionKey: conv.section_key ?? null,
      fieldKey: conv.field_key ?? null,
      messages: [{
        id: msg.id,
        role: "assistant",
        content: opening,
        createdAt: msg.created_at,
        suggestion: null,
      }],
      context: ctx,
    };
  });

// ---------- send message ----------

export type SendAIMessageInput = { conversationId: string; text: string };

export const sendAIMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: SendAIMessageInput) => {
    if (!d?.conversationId) throw new Error("conversationId obrigatório");
    const t = (d?.text ?? "").trim();
    if (!t) throw new Error("Digite uma mensagem.");
    if (t.length > AI_LIMITS.MAX_INPUT_CHARS) throw new Error("Mensagem muito longa.");
    return { conversationId: d.conversationId, text: t };
  })
  .handler(async ({ data, context }): Promise<{ userMessage: AIConversationMessage; assistantMessage: AIConversationMessage }> => {
    const { supabase, userId } = context;
    await requireFlag(supabase, "ai_consultant_enabled");
    await enforceRateLimits(supabase, userId, "message");

    const { data: conv } = await supabase
      .from("ai_conversations")
      .select("id, project_id, section_key, field_key")
      .eq("id", data.conversationId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!conv) throw new Error("Conversa não encontrada.");

    const ctx = await buildAIContext(supabase, userId, {
      projectId: conv.project_id,
      sectionKey: (conv.section_key ?? null) as BlockKey | null,
      fieldKey: conv.field_key ?? null,
    });

    // Snapshot para controle de concorrência.
    const { data: wsRow } = conv.project_id
      ? await supabase.from("business_workspace")
          .select("id, updated_at")
          .eq("project_id", conv.project_id)
          .maybeSingle()
      : { data: null };

    const originalValue = ctx.section && conv.field_key
      ? (ctx.section.currentValues[conv.field_key] ?? null)
      : null;

    // Histórico limitado (últimos 20 turns; garante que sugestões aplicadas sobrevivem).
    const { data: history } = await supabase
      .from("ai_messages")
      .select("id, role, content, created_at")
      .eq("conversation_id", data.conversationId)
      .order("created_at", { ascending: false })
      .limit(20);

    const historyAsc = (history ?? []).reverse();
    const intent: ChatIntent = conv.field_key ? "field_suggestion" : "conversation";
    const system = buildSystemPrompt(ctx, intent);
    const messages = [
      { role: "system" as const, content: system },
      ...historyAsc.map((m: any) => ({ role: m.role as "user" | "assistant", content: m.content })),
      { role: "user" as const, content: data.text },
    ];

    // Persistir user turn primeiro.
    const { data: userMsgRow, error: userErr } = await supabase
      .from("ai_messages")
      .insert({
        conversation_id: data.conversationId,
        user_id: userId,
        role: "user",
        content: data.text,
      })
      .select("id, created_at")
      .single();
    if (userErr) throw userErr;
    await trackAI(supabase, userId, "ai_message_sent", { conversation_id: data.conversationId });

    // SDE.4 · Onda 4.3b — Rate-limit ANTES da quota (precedência declarada).
    // Se bloqueia, NÃO consome quota. Mensagem humana distinta.
    const rate: RateLimitResult = await checkIaRateLimit(supabase, userId);
    if (!rate.allowed && rate.humanMessage) {
      const blockedContent = rate.humanMessage;
      const { data: blockedRow, error: bErr } = await supabase
        .from("ai_messages")
        .insert({
          conversation_id: data.conversationId,
          user_id: userId,
          role: "assistant",
          content: blockedContent,
        })
        .select("id, created_at")
        .single();
      if (bErr) throw bErr;
      await trackAI(supabase, userId, "ia_ratelimit_blocked", {
        conversation_id: data.conversationId,
        limit: rate.limit,
        window_seconds: rate.windowSeconds,
        retry_after_seconds: rate.retryAfterSeconds,
      });
      await logAIAction(supabase, {
        userId, projectId: conv.project_id, conversationId: conv.id,
        actionType: "chat_reply", mode: intent, success: false,
        errorType: "ratelimit_blocked",
      });
      return {
        userMessage: {
          id: userMsgRow.id, role: "user", content: data.text,
          createdAt: userMsgRow.created_at, suggestion: null,
        },
        assistantMessage: {
          id: blockedRow.id, role: "assistant", content: blockedContent,
          createdAt: blockedRow.created_at, suggestion: null,
        },
      };
    }

    // SDE.4 · Onda 4.3a — Gate de quota ANTES do stream. Wrapper externo ao HIA.1.
    // Se bloquear, respondemos com mensagem humana pronta e NUNCA chamamos o provider.
    const quota: QuotaGateResult = await consumeIaQuota(supabase, userId);
    if (!quota.allowed && quota.humanMessage) {
      const blockedContent = quota.humanMessage;
      const { data: blockedRow, error: bErr } = await supabase
        .from("ai_messages")
        .insert({
          conversation_id: data.conversationId,
          user_id: userId,
          role: "assistant",
          content: blockedContent,
        })
        .select("id, created_at")
        .single();
      if (bErr) throw bErr;
      await trackAI(supabase, userId, "ai_quota_reached", {
        conversation_id: data.conversationId,
        limit: quota.limit,
        is_new_user: quota.isNewUser,
      });
      await logAIAction(supabase, {
        userId, projectId: conv.project_id, conversationId: conv.id,
        actionType: "chat_reply", mode: intent, success: false,
        errorType: "quota_reached",
      });
      return {
        userMessage: {
          id: userMsgRow.id, role: "user", content: data.text,
          createdAt: userMsgRow.created_at, suggestion: null,
        },
        assistantMessage: {
          id: blockedRow.id, role: "assistant", content: blockedContent,
          createdAt: blockedRow.created_at, suggestion: null,
        },
      };
    }

    // Chamar provider + parse com reparo.
    let llm: LLMResult;
    try {
      llm = await llmChat(messages);
    } catch (e) {
      const err = friendlyError(e);
      await logAIAction(supabase, {
        userId, projectId: conv.project_id, conversationId: conv.id,
        actionType: "chat_reply", mode: intent, success: false,
        errorType: e instanceof AIError ? e.type : "internal",
      });
      throw err;
    }

    const parsed = await parseWithRepair(
      llm.text,
      AIConversationalReplySchema,
      async () => llmChat([...messages, { role: "assistant", content: llm.text }, { role: "user", content: repairPrompt(llm.text) }]),
    );

    let content = "Não consegui gerar uma resposta válida agora. Tente reformular sua pergunta.";
    let suggestionMeta: AIConversationMessage["suggestion"] = null;

    if ("value" in parsed) {
      content = parsed.value.content;
      const s = parsed.value.suggestion ?? null;
      if (s && s.content?.trim()) {
        suggestionMeta = await persistSuggestion(supabase, {
          conversationId: conv.id,
          userId,
          projectId: conv.project_id,
          sectionKey: conv.section_key ?? s.sectionKey ?? null,
          fieldKey: conv.field_key ?? s.fieldKey ?? null,
          suggestion: s,
          originalValue,
          workspaceUpdatedAt: (wsRow as any)?.updated_at ?? null,
          contextHash: hashContext(JSON.stringify(ctx.section?.currentValues ?? {})),
        });
      }
    } else {
      await logAIAction(supabase, {
        userId, projectId: conv.project_id, conversationId: conv.id,
        actionType: "chat_reply", mode: intent, success: false, errorType: "invalid_response",
        llm,
      });
    }

    const { data: assistantRow, error: aErr } = await supabase
      .from("ai_messages")
      .insert({
        conversation_id: data.conversationId,
        user_id: userId,
        role: "assistant",
        content,
        parts: suggestionMeta ? ([{ type: "suggestion", suggestionId: suggestionMeta.id }] as never) : ([] as never),
      })
      .select("id, created_at")
      .single();
    if (aErr) throw aErr;

    await supabase.from("ai_conversations")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", data.conversationId);

    await logAIAction(supabase, {
      userId, projectId: conv.project_id, conversationId: conv.id,
      actionType: "chat_reply", mode: intent, success: "value" in parsed,
      errorType: "value" in parsed ? null : "invalid_response",
      llm,
    });

    // Gatilho de sumarização por volume (best-effort; nunca bloqueia a resposta).
    try { await maybeSummarize(supabase, userId, conv.id, "message_threshold"); } catch {}

    return {
      userMessage: {
        id: userMsgRow.id, role: "user", content: data.text,
        createdAt: userMsgRow.created_at, suggestion: null,
      },
      assistantMessage: {
        id: assistantRow.id, role: "assistant", content,
        createdAt: assistantRow.created_at, suggestion: suggestionMeta,
      },
    };
  });

async function persistSuggestion(supabase: any, args: {
  conversationId: string;
  userId: string;
  projectId: string | null;
  sectionKey: string | null;
  fieldKey: string | null;
  suggestion: AIFieldSuggestion;
  originalValue: string | null;
  workspaceUpdatedAt: string | null;
  contextHash: string;
}): Promise<AIConversationMessage["suggestion"]> {
  const { data: sug } = await supabase
    .from("ai_suggestions")
    .insert({
      conversation_id: args.conversationId,
      user_id: args.userId,
      project_id: args.projectId,
      section_key: args.sectionKey,
      field_key: args.fieldKey,
      payload: { text: args.suggestion.content, reasoning: args.suggestion.reasoningSummary } as never,
      status: "generated",
      confidence: args.suggestion.confidence ?? "medium",
      warnings: (args.suggestion.warnings ?? []) as never,
      original_value: args.originalValue,
      workspace_updated_at: args.workspaceUpdatedAt,
      context_hash: args.contextHash,
    })
    .select("id, confidence, warnings")
    .single();
  if (!sug) return null;
  await trackAI(supabase, args.userId, "ai_suggestion_generated", {
    conversation_id: args.conversationId,
    suggestion_id: sug.id,
    section_key: args.sectionKey,
    field_key: args.fieldKey,
    confidence: sug.confidence,
  });
  return {
    id: sug.id,
    text: args.suggestion.content,
    sectionKey: args.sectionKey,
    fieldKey: args.fieldKey,
    confidence: sug.confidence,
    warnings: (sug.warnings as string[]) ?? [],
    conflict: false,
  };
}

// ---------- list conversations ----------

export const listAIConversations = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { sectionKey?: string | null }) => d ?? {})
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    let q = supabase.from("ai_conversations")
      .select("id, title, section_key, field_key, updated_at")
      .eq("user_id", userId).is("archived_at", null)
      .order("updated_at", { ascending: false }).limit(20);
    if (data.sectionKey) q = q.eq("section_key", data.sectionKey);
    const { data: rows } = await q;
    return (rows ?? []).map((r: any) => ({
      id: r.id, title: r.title,
      sectionKey: r.section_key, fieldKey: r.field_key,
      updatedAt: r.updated_at,
    }));
  });

// ---------- get conversation ----------

export const getAIConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { conversationId: string }) => {
    if (!d?.conversationId) throw new Error("conversationId obrigatório");
    return d;
  })
  .handler(async ({ data, context }): Promise<AIConversationView | null> => {
    const { supabase, userId } = context;
    const { data: conv } = await supabase
      .from("ai_conversations")
      .select("id, title, section_key, field_key, project_id, summary, summary_version")
      .eq("id", data.conversationId).eq("user_id", userId).maybeSingle();
    if (!conv) return null;

    const { data: rows } = await supabase
      .from("ai_messages")
      .select("id, role, content, parts, created_at")
      .eq("conversation_id", data.conversationId)
      .order("created_at", { ascending: true });

    const suggestionIds = (rows ?? [])
      .flatMap((r: any) => (Array.isArray(r.parts) ? r.parts : []))
      .filter((p: any) => p?.type === "suggestion")
      .map((p: any) => p.suggestionId);
    const sugMap = new Map<string, any>();
    if (suggestionIds.length) {
      const { data: sugs } = await supabase.from("ai_suggestions")
        .select("id, payload, section_key, field_key, status, confidence, warnings")
        .in("id", suggestionIds);
      for (const s of sugs ?? []) sugMap.set((s as any).id, s);
    }

    const messages: AIConversationMessage[] = (rows ?? []).map((r: any) => {
      const parts = Array.isArray(r.parts) ? r.parts : [];
      const sPart = parts.find((p: any) => p?.type === "suggestion");
      const s = sPart ? sugMap.get(sPart.suggestionId) : null;
      return {
        id: r.id, role: r.role, content: r.content, createdAt: r.created_at,
        suggestion: s ? {
          id: s.id,
          text: (s.payload as any)?.text ?? "",
          sectionKey: s.section_key ?? null,
          fieldKey: s.field_key ?? null,
          confidence: s.confidence ?? undefined,
          warnings: (s.warnings as string[]) ?? [],
          conflict: false,
        } : null,
      };
    });

    const ctx = await buildAIContext(supabase, userId, {
      projectId: conv.project_id,
      sectionKey: (conv.section_key ?? null) as BlockKey | null,
      fieldKey: conv.field_key ?? null,
    });

    const summary = ((): AIConversationSummaryView => {
      const s = (conv as any).summary;
      if (!s || typeof s !== "object") return null;
      return {
        goal: String(s.goal ?? ""),
        confirmedFacts: Array.isArray(s.confirmedFacts) ? s.confirmedFacts.slice(0, 8) : [],
        openQuestions: Array.isArray(s.openQuestions) ? s.openQuestions.slice(0, 5) : [],
        nextStep: String(s.nextStep ?? ""),
        version: Number((conv as any).summary_version ?? s.summaryVersion ?? 1),
      };
    })();

    return {
      conversationId: conv.id, title: conv.title,
      sectionKey: conv.section_key ?? null, fieldKey: conv.field_key ?? null,
      messages, context: ctx, summary,
    };
  });

// ---------- apply suggestion ----------

const ALLOWED_BLOCKS = new Set([
  "model", "audience", "niche", "offer", "products",
  "pricing", "brand", "channels", "sales_plan",
]);

export type ApplyAISuggestionInput = {
  suggestionId: string;
  editedText?: string | null;
  force?: boolean; // usuário confirmou aplicar mesmo com conflito
};

export type ApplyAISuggestionResult =
  | { ok: true; sectionKey: string | null; fieldKey: string | null; status: "applied" | "edited" }
  | { ok: false; conflict: true; currentValue: string | null; suggestionText: string }
  | { ok: false; criticalConflict: true; issues: ConsistencyIssue[]; suggestionText: string };

export const applyAISuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: ApplyAISuggestionInput) => {
    if (!d?.suggestionId) throw new Error("suggestionId obrigatório");
    return d;
  })
  .handler(async ({ data, context }): Promise<ApplyAISuggestionResult> => {
    const { supabase, userId } = context;

    if (!isActionAllowed("update_field")) {
      throw new Error("Ação não permitida pelo registry.");
    }
    await requireFlag(supabase, "ai_field_suggestions_enabled");

    // Autorização + escopo: sugestão deve ser do usuário.
    const { data: sug } = await supabase
      .from("ai_suggestions")
      .select("id, conversation_id, project_id, section_key, field_key, payload, status, original_value, workspace_updated_at")
      .eq("id", data.suggestionId).eq("user_id", userId).maybeSingle();
    if (!sug) throw new Error("Sugestão não encontrada.");

    // Idempotência: aplicada já não reaplica.
    if (sug.status === "applied" || sug.status === "edited") {
      return { ok: true, sectionKey: sug.section_key, fieldKey: sug.field_key, status: sug.status };
    }
    if (sug.status === "rejected" || sug.status === "expired") {
      throw new Error("Esta sugestão não pode mais ser aplicada. Gere uma nova.");
    }

    // Validar conversa/projeto pertencem ao usuário.
    if (sug.conversation_id) {
      const { data: conv } = await supabase.from("ai_conversations")
        .select("id").eq("id", sug.conversation_id).eq("user_id", userId).maybeSingle();
      if (!conv) throw new Error("Conversa inválida.");
    }

    const sectionKey = sug.section_key as string | null;
    const fieldKey = sug.field_key as string | null;
    const text = ((data.editedText ?? (sug.payload as any)?.text) ?? "").toString().trim();
    if (!text) throw new Error("Sugestão vazia.");
    if (text.length > AI_LIMITS.MAX_OUTPUT_CHARS) throw new Error("Sugestão excede o tamanho máximo.");

    if (!sectionKey || !ALLOWED_BLOCKS.has(sectionKey) || !fieldKey || !sug.project_id) {
      throw new Error("Alvo da sugestão inválido.");
    }

    const config = getStructuredConfig(sectionKey as BlockKey);
    const field = config?.fields.find((f) => f.key === fieldKey);
    if (!config || !field) throw new Error("Campo alvo não existe mais.");

    // Concorrência + snapshot completo do workspace.
    const { data: ws } = await supabase.from("business_workspace")
      .select("*")
      .eq("project_id", sug.project_id).maybeSingle();
    if (!ws) throw new Error("Workspace inexistente.");

    const currentBlock = (((ws as any)[config.block]) ?? {}) as Record<string, unknown>;
    const currentRaw = currentBlock[fieldKey];
    const currentAsText = currentRaw == null ? "" :
      (typeof currentRaw === "string" ? currentRaw : JSON.stringify(currentRaw));
    const originalAsText = (sug.original_value ?? "").toString();

    const conflicted =
      !data.force &&
      currentAsText.trim().length > 0 &&
      currentAsText.trim() !== originalAsText.trim();

    if (conflicted) {
      return {
        ok: false,
        conflict: true,
        currentValue: currentAsText,
        suggestionText: text,
      };
    }

    // Consistência determinística — pode bloquear quando cria inconsistência crítica nova.
    const blockingFlag = await isFlagOn(supabase, "ai_critical_conflict_blocking_enabled");
    if (blockingFlag) {
      const baseline: BusinessSnapshot = {
        model: (ws as any).model ?? null,
        niche: (ws as any).niche ?? null,
        audience: (ws as any).audience ?? null,
        offer: (ws as any).offer ?? null,
        products: (ws as any).products ?? null,
        pricing: (ws as any).pricing ?? null,
        brand: (ws as any).brand ?? null,
        channels: (ws as any).channels ?? null,
        sales_plan: (ws as any).sales_plan ?? null,
      };
      const newCrit = findCriticalConflictsForSuggestion(baseline, sectionKey, fieldKey, text);
      if (newCrit.length > 0) {
        await logAIAction(supabase, {
          userId, projectId: sug.project_id, conversationId: sug.conversation_id,
          actionType: "apply_suggestion_blocked", mode: "update_field",
          payload: { suggestion_id: data.suggestionId, issues: newCrit.map((i) => i.id) },
          success: false, errorType: "critical_conflict",
        });
        return { ok: false, criticalConflict: true, issues: newCrit, suggestionText: text };
      }
    }

    const nextValue = serializeFieldValue(field, text);
    const nextBlock = { ...currentBlock, [fieldKey]: nextValue, _draft_updated_at: new Date().toISOString() };
    const { error: uErr } = await (supabase as any)
      .from("business_workspace")
      .update({ [config.block]: nextBlock })
      .eq("id", (ws as any).id);
    if (uErr) throw uErr;

    const nextStatus = data.editedText ? "edited" : "applied";
    await supabase.from("ai_suggestions").update({
      status: nextStatus,
      applied_at: new Date().toISOString(),
      applied_text: text,
      updated_at: new Date().toISOString(),
    }).eq("id", data.suggestionId).in("status", ["generated", "pending"]);

    await logAIAction(supabase, {
      userId, projectId: sug.project_id, conversationId: sug.conversation_id,
      actionType: "apply_suggestion", mode: "update_field",
      payload: { suggestion_id: data.suggestionId, section_key: sectionKey, field_key: fieldKey, forced: !!data.force },
      result: { text },
      success: true,
    });
    await trackAI(supabase, userId, "ai_suggestion_applied", {
      suggestion_id: data.suggestionId,
      section_key: sectionKey, field_key: fieldKey,
      edited: !!data.editedText, forced: !!data.force,
    });

    return { ok: true, sectionKey, fieldKey, status: nextStatus };
  });

// Rejeitar sugestão (idempotente).
export const rejectAISuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { suggestionId: string }) => {
    if (!d?.suggestionId) throw new Error("suggestionId obrigatório");
    return d;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await supabase.from("ai_suggestions")
      .update({ status: "rejected", updated_at: new Date().toISOString() })
      .eq("id", data.suggestionId).eq("user_id", userId)
      .in("status", ["generated", "pending"]);
    await trackAI(supabase, userId, "ai_suggestion_rejected", { suggestion_id: data.suggestionId });
    return { ok: true };
  });

// ---------- review whole plan ----------

function deterministicReview(ctx: AIContext) {
  const total = ctx.planSummary.length || 1;
  const completed = ctx.planSummary.filter((s) => s.status === "completa");
  const inProgress = ctx.planSummary.filter((s) => s.status === "em construção");
  const empty = ctx.planSummary.filter((s) => s.status === "vazia");
  const score = Math.round((completed.length / total) * 100);
  return {
    score,
    completed: completed.map((s) => s.title),
    inProgress: inProgress.map((s) => s.title),
    empty: empty.map((s) => s.title),
  };
}

export const runAIReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ReviewReport> => {
    const { supabase, userId } = context;
    await requireFlag(supabase, "ai_global_review_enabled");
    await enforceRateLimits(supabase, userId, "review");

    const projectId = await getActiveProjectId(supabase, userId);
    if (!projectId) throw new Error("Nenhum projeto ativo.");

    const ctx = await buildAIContext(supabase, userId, { projectId });
    const diagnostic = deterministicReview(ctx);

    // Consistência determinística (independente da IA).
    const { data: ws } = await supabase.from("business_workspace")
      .select("model, niche, audience, offer, products, pricing, brand, channels, sales_plan")
      .eq("project_id", projectId).maybeSingle();
    const snap = (ws ?? {}) as unknown as BusinessSnapshot;
    const checksOn = await isFlagOn(supabase, "ai_consistency_checks_enabled");
    const issues: ConsistencyIssue[] = checksOn ? runAllChecks(snap) : [];
    const consistency = consistencyScore(issues);
    const critical = issues.filter((i) => i.severity === "critical");
    const warnings = issues.filter((i) => i.severity === "warning");

    const { system, user } = buildReviewPrompt(ctx, diagnostic);
    await trackAI(supabase, userId, "ai_review_started", { project_id: projectId });

    // Base determinística garantida mesmo se a IA falhar.
    const report: ReviewReport = {
      score: diagnostic.score,
      consistencyScore: consistency,
      interpretationScore: null,
      strengths: [],
      opportunities: [
        ...critical.map((i) => `${i.title}: ${i.description}`),
        ...diagnostic.inProgress.map((t) => `Complete a seção "${t}".`),
        ...diagnostic.empty.map((t) => `Ainda não iniciou "${t}".`),
      ].slice(0, 6),
      suggestions: [],
      criticalIssues: critical.map((i) => ({
        id: i.id, title: i.title, description: i.description,
        sections: i.sections, fields: i.fields,
      })),
      warningIssues: warnings.map((i) => ({
        id: i.id, title: i.title, description: i.description, sections: i.sections,
      })),
    };

    let llm: LLMResult | null = null;
    try {
      const issueBrief = issues.length
        ? `\n\nINCONSISTÊNCIAS DETERMINÍSTICAS (fonte de verdade — apenas explique/priorize, não altere severidade):\n${issues.slice(0, 10).map((i) => `- [${i.severity}] ${i.title} — ${i.description}`).join("\n")}`
        : "";
      llm = await llmChat(
        [{ role: "system", content: system }, { role: "user", content: user + issueBrief }],
        { maxTokens: 900 },
      );
      const parsed = await parseWithRepair(
        llm.text,
        AIReviewSchema,
        async () => llmChat([
          { role: "system", content: system },
          { role: "user", content: user + issueBrief },
          { role: "assistant", content: llm!.text },
          { role: "user", content: repairPrompt(llm!.text) },
        ], { maxTokens: 900 }),
      );
      if ("value" in parsed) {
        report.strengths = parsed.value.strengths;
        report.opportunities = parsed.value.opportunities.length ? parsed.value.opportunities : report.opportunities;
        report.suggestions = parsed.value.suggestions;
        report.interpretationScore = parsed.value.interpretationScore ?? null;
      }
    } catch (e) {
      await logAIAction(supabase, {
        userId, projectId, actionType: "review_plan", mode: "review",
        success: false, errorType: e instanceof AIError ? e.type : "internal",
      });
    }

    await logAIAction(supabase, {
      userId, projectId, actionType: "review_plan", mode: "review",
      payload: { diagnostic, consistency }, result: report,
      success: true, llm,
    });
    await trackAI(supabase, userId, "ai_review_finished", {
      project_id: projectId,
      score: report.score,
      consistency_score: report.consistencyScore,
      interpretation_score: report.interpretationScore,
      critical_count: critical.length,
      warning_count: warnings.length,
    });

    return report;
  });

// ---------- consistency-only report (para /pendencias / observador / seção) ----------

export const getConsistencyReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { sectionKey?: string | null } | undefined) => d ?? {})
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const projectId = await getActiveProjectId(supabase, userId);
    if (!projectId) return { score: 100, issues: [] as ConsistencyIssue[], projectId: null };
    const { data: ws } = await supabase.from("business_workspace")
      .select("model, niche, audience, offer, products, pricing, brand, channels, sales_plan")
      .eq("project_id", projectId).maybeSingle();
    const all = runAllChecks((ws ?? {}) as unknown as BusinessSnapshot);
    const filtered = data.sectionKey
      ? all.filter((i) => i.sections.includes(data.sectionKey as string))
      : all;
    return { score: consistencyScore(all), issues: filtered, projectId };
  });

// ---------- regenerar sugestão ----------

const REGEN_REASONS = new Set(["more_specific", "shorter", "adapt_audience", "fix_inconsistency", "other_approach", "custom"]);

export const regenerateAISuggestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    suggestionId: string;
    reason?: string;
    instruction?: string;
    previousWarnings?: string[];
    conflictIds?: string[];
    currentValue?: string | null;
  }) => {
    if (!d?.suggestionId) throw new Error("suggestionId obrigatório");
    return d;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await requireFlag(supabase, "ai_field_suggestions_enabled");

    const { data: prev } = await supabase.from("ai_suggestions")
      .select("id, conversation_id, project_id, section_key, field_key, payload, regeneration_count")
      .eq("id", data.suggestionId).eq("user_id", userId).maybeSingle();
    if (!prev) throw new Error("Sugestão original não encontrada.");
    if ((prev.regeneration_count ?? 0) >= AI_LIMITS.REGENERATIONS_PER_SUGGESTION) {
      throw new Error("Limite de regenerações desta sugestão atingido.");
    }

    const reason = data.reason && REGEN_REASONS.has(data.reason) ? data.reason : "other_approach";
    const previousText = (prev.payload as any)?.text ?? "";

    // Marcar antiga como expirada e incrementar contagem.
    await supabase.from("ai_suggestions").update({
      status: "expired",
      regeneration_count: (prev.regeneration_count ?? 0) + 1,
      updated_at: new Date().toISOString(),
    }).eq("id", data.suggestionId).in("status", ["generated", "pending"]);

    // Construir mensagem-hint para enviar como nova user turn no drawer.
    const reasonLabel: Record<string, string> = {
      more_specific: "torne mais específico",
      shorter: "torne mais curto",
      adapt_audience: "adapte melhor ao público-alvo",
      fix_inconsistency: "corrija a inconsistência apontada",
      other_approach: "proponha uma abordagem diferente",
      custom: data.instruction?.slice(0, 400) ?? "revise a sugestão anterior",
    };
    const context_bits: string[] = [];
    if (previousText) context_bits.push(`Sugestão anterior: "${previousText.slice(0, 240)}"`);
    if (data.currentValue) context_bits.push(`Valor atualmente salvo: "${String(data.currentValue).slice(0, 240)}"`);
    if (data.previousWarnings?.length) context_bits.push(`Avisos anteriores: ${data.previousWarnings.slice(0, 3).join(" · ")}`);
    if (data.conflictIds?.length) context_bits.push(`Inconsistências detectadas: ${data.conflictIds.slice(0, 3).join(", ")}`);
    const hint = `Gere uma nova sugestão para este campo — ${reasonLabel[reason]}.\n${context_bits.join("\n")}`;

    await trackAI(supabase, userId, "ai_regeneration_requested", {
      suggestion_id: data.suggestionId,
      conversation_id: prev.conversation_id,
      reason,
      count: (prev.regeneration_count ?? 0) + 1,
    });
    return {
      ok: true,
      conversationId: prev.conversation_id,
      hint,
      reason,
    };
  });

// ---------- sumarização de conversa ----------

const SUMMARY_MESSAGE_THRESHOLD = 20;

async function maybeSummarize(supabase: any, userId: string, conversationId: string, trigger: "message_threshold" | "section_completed" | "important_suggestion" | "manual" | "reopen_stale") {
  const on = await isFlagOn(supabase, "ai_conversation_summaries_enabled");
  if (!on) return;

  const { data: conv } = await supabase.from("ai_conversations")
    .select("id, summary, summary_version, summarized_until_message_id, summary_updated_at")
    .eq("id", conversationId).eq("user_id", userId).maybeSingle();
  if (!conv) return;

  // Buscar mensagens ainda não sumarizadas.
  let q = supabase.from("ai_messages")
    .select("id, role, content, created_at")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });
  const { data: allMsgs } = await q;
  const msgs = (allMsgs ?? []) as any[];
  const cutoffIdx = conv.summarized_until_message_id
    ? msgs.findIndex((m) => m.id === conv.summarized_until_message_id) + 1
    : 0;
  const newMsgs = msgs.slice(cutoffIdx);

  // Só sumariza se o gatilho justifica.
  if (trigger === "message_threshold" && newMsgs.length < SUMMARY_MESSAGE_THRESHOLD) return;
  if (newMsgs.length === 0) return;

  const previousSummary = conv.summary
    ? (() => { try { return ConversationSummarySchema.parse(conv.summary); } catch { return null; } })()
    : null;

  const result = await summarizeConversation({
    previousSummary,
    messages: newMsgs.map((m) => ({ role: m.role, content: m.content })),
    trigger,
  });

  if (!result.ok) {
    await logAIAction(supabase, {
      userId, projectId: null, conversationId,
      actionType: "summarize_conversation", mode: "summary",
      success: false, errorType: result.error,
    });
    return;
  }

  const lastMsgId = msgs[msgs.length - 1]?.id ?? null;
  await supabase.from("ai_conversations").update({
    summary: result.summary as never,
    summary_version: result.summary.summaryVersion,
    summarized_until_message_id: lastMsgId,
    summary_updated_at: new Date().toISOString(),
  }).eq("id", conversationId);

  await logAIAction(supabase, {
    userId, projectId: null, conversationId,
    actionType: "summarize_conversation", mode: "summary",
    payload: { trigger, message_count: newMsgs.length },
    result: { version: result.summary.summaryVersion },
    success: true,
    llm: {
      text: "", model: result.llm.model, provider: "lovable",
      latencyMs: result.llm.latencyMs,
      inputTokens: result.llm.inputTokens, outputTokens: result.llm.outputTokens,
      totalTokens: result.llm.inputTokens + result.llm.outputTokens,
    },
  });
}

export const summarizeAIConversation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { conversationId: string }) => {
    if (!d?.conversationId) throw new Error("conversationId obrigatório");
    return d;
  })
  .handler(async ({ data, context }) => {
    await maybeSummarize(context.supabase, context.userId, data.conversationId, "manual");
    return { ok: true };
  });
